import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  FileText,
  PenTool,
  Check,
  AlertCircle,
  ArrowLeft,
  Eraser
} from 'lucide-react'
import api from '../config/api'
import { formatDate } from '../utils/formatDate'
import '../styles/SignDocument.css'

function SignDocument() {
  const { signatureId } = useParams()
  const navigate = useNavigate()
  const canvasRef = useRef(null)
  const [signature, setSignature] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [signing, setSigning] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasSignature, setHasSignature] = useState(false)

  useEffect(() => {
    fetchSignature()
  }, [signatureId])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    ctx.strokeStyle = '#1a1a2e'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [signature])

  const fetchSignature = async () => {
    try {
      setLoading(true)
      const response = await api.get('/signatures/pending')
      const sig = response.data.data.find(s => s.id === signatureId)
      if (!sig) {
        setError('Signature request not found or already completed')
      } else {
        setSignature(sig)
      }
    } catch (err) {
      setError('Failed to load signature request')
    } finally {
      setLoading(false)
    }
  }

  const startDrawing = (e) => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const rect = canvas.getBoundingClientRect()
    const x = (e.clientX || e.touches[0].clientX) - rect.left
    const y = (e.clientY || e.touches[0].clientY) - rect.top

    ctx.beginPath()
    ctx.moveTo(x, y)
    setIsDrawing(true)
    setHasSignature(true)
  }

  const draw = (e) => {
    if (!isDrawing) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const rect = canvas.getBoundingClientRect()
    const x = (e.clientX || e.touches[0].clientX) - rect.left
    const y = (e.clientY || e.touches[0].clientY) - rect.top

    ctx.lineTo(x, y)
    ctx.stroke()
  }

  const stopDrawing = () => {
    setIsDrawing(false)
  }

  const clearSignature = () => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setHasSignature(false)
  }

  const handleSubmit = async () => {
    if (!agreed) {
      setError('Please agree to the terms before signing')
      return
    }

    if (!hasSignature) {
      setError('Please provide your signature')
      return
    }

    try {
      setSigning(true)
      const canvas = canvasRef.current
      const signatureData = canvas.toDataURL('image/png')

      await api.post(`/signatures/${signatureId}/sign`, {
        signatureData,
        agreedToTerms: true
      })

      navigate('/documents', {
        state: { success: 'Document signed successfully!' }
      })
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit signature')
    } finally {
      setSigning(false)
    }
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading document...</p>
      </div>
    )
  }

  if (error && !signature) {
    return (
      <div className="error-container">
        <AlertCircle size={48} />
        <h2>Unable to Load Document</h2>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={() => navigate('/documents')}>
          Back to Documents
        </button>
      </div>
    )
  }

  return (
    <div className="sign-document-page">
      <header className="page-header">
        <button className="back-link" onClick={() => navigate('/documents')}>
          <ArrowLeft size={20} />
          Back to Documents
        </button>
      </header>

      <motion.div
        className="sign-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="document-preview">
          <div className="preview-header">
            <FileText size={24} />
            <div>
              <h2>{signature?.documentName}</h2>
              <p>Please review and sign this document</p>
            </div>
          </div>

          {signature?.expiresAt && (
            <div className="expiry-notice">
              <AlertCircle size={16} />
              <span>
                This signature request expires on{' '}
                {formatDate(signature.expiresAt)}
              </span>
            </div>
          )}
        </div>

        <div className="signature-section">
          <h3>Your Signature</h3>
          <p className="signature-instructions">
            Use your mouse or finger to sign in the box below
          </p>

          <div className="signature-canvas-wrapper">
            <canvas
              ref={canvasRef}
              width={400}
              height={150}
              className="signature-canvas"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            <button
              className="clear-btn"
              onClick={clearSignature}
              title="Clear signature"
            >
              <Eraser size={18} />
              Clear
            </button>
          </div>

          <label className="terms-checkbox">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span>
              I agree that this electronic signature is legally binding and has
              the same legal effect as a handwritten signature.
            </span>
          </label>

          {error && (
            <div className="alert alert-error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <div className="action-buttons">
            <button
              className="btn btn-outline"
              onClick={() => navigate('/documents')}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={signing || !agreed || !hasSignature}
            >
              {signing ? (
                'Submitting...'
              ) : (
                <>
                  <PenTool size={18} />
                  Sign Document
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default SignDocument
